import {
  gray1,
  gray2,
  gray5,
  gray6,
  mainColor,
  white,
} from "assets/styleUtils";
import { FontText } from "components/FontText";
import Input from "components/Input/Input";
import { useCallbackState } from "hooks/useCallbackState";
import useUser from "hooks/useUser";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { signOnState } from "recoil/atoms";
import { post } from "utils/ApiUtil";
import { formatStringToDate, isDefined } from "utils/common";
import styles from "./style.module.scss";
import { headerState } from "recoil/header";
import { Icon } from "components";
import Modal from "components/Modal";
import { SvgLineArrow } from "assets";
import SvgcheckLine from "assets/svgIcons/SvgcheckLine";

const SIGNUP_REASON_LIST: Readonly<
  {
    title: string;
    type?: "select" | "input";
    selectList?: string[];
  }[]
> = [
  { title: "웨딩홀" },
  { title: "플래닝", type: "select", selectList: ["동행", "비동행"] },
  { title: "스드메 (스튜디오, 드레스, 메이크업)" },
  { title: "본식 스냅 & 본식 DVD" },
  { title: "혼수 (예물, 예복, 한복, 허니문)" },
  { title: "웨딩 컨텐츠" },
  { title: "커뮤니티 활동" },
  { title: "기타", type: "input" },
];

const SIGNUP_ROUTE_LIST: Readonly<
  {
    title: string;
    isRecommend?: boolean;
  }[]
> = [
  { title: "인스타그램" },
  { title: "페이스북" },
  { title: "네이버검색 (카페/후기 등)" },
  { title: "지인추천", isRecommend: true },
];

type TGender = "남자" | "여자";
interface ISignupRequest {
  sns_type: string;
  sns_id: string;
  sns_token: string;
  name: string;
  nickname: string;
  gender: TGender;
  phone_number: string;
  recommender: string;
  cafenickname: string;
  cafegrade: string;
  wedding_yn: "Y" | "N";
  wedding_hope_location: string;
  wedding_day: string;
  wedding_anniversary: string;
  signup_reason: string;
  signup_reason_etc: string;
  signup_route: string;
}
interface ISignupData extends ISignupRequest {
  authCode: string;
  isAuthConfirm: boolean;
  userInputAuthCode: string;
  isCheckedNickname: boolean;
  nicknameErrorMsg?: string;
}

interface IPageProps {
  data: ISignupData;
  setData: (value: ISignupData, callback?: () => void) => void;
  setToast?: (text: string) => void;
  keyboardHeight?: number;
}

type TTermsKey = "service" | "private";
interface ITerms {
  key: TTermsKey;
  title: string;
  link: string;
  require: boolean;
}
const TERMS_LIST: Readonly<ITerms[]> = [
  {
    key: "service",
    title: "서비스 이용약관에 동의",
    link: "https://makemywedding.s3.ap-northeast-2.amazonaws.com/terms/terms_of_service.pdf",
    require: true,
  },
  {
    key: "private",
    title: "개인정보 수집 이용에 동의",
    link: "https://makemywedding.s3.ap-northeast-2.amazonaws.com/terms/privacy_policy.pdf",
    require: true,
  },
];

export default function SignOnPage() {
  const setHeader = useSetRecoilState(headerState);
  const signOnData = useRecoilValue(signOnState);
  const { login } = useUser();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [requestData, setRequestData] = useCallbackState<ISignupData>({
    sns_type: signOnData?.type!,
    sns_id: signOnData?.id!,
    sns_token: signOnData?.token!,
    name: "",
    nickname: "",
    gender: "여자",
    phone_number: "",
    recommender: "",
    cafenickname: "",
    cafegrade: "",
    wedding_yn: "N",
    wedding_hope_location: "",
    wedding_day: "",
    wedding_anniversary: "",
    signup_reason: "",
    signup_reason_etc: "",
    signup_route: "",
    authCode: "",
    userInputAuthCode: "",
    isAuthConfirm: false,
    isCheckedNickname: false,
  });
  const [isOpenModal, setIsOpenModal] = useState(false);

  const disabled: boolean = useMemo(() => {
    let returnValue = false;
    if (step === 0) {
      const selectedSignupReasonObj = SIGNUP_REASON_LIST.find(
        (item) => item.title === requestData.signup_reason,
      );
      if (!selectedSignupReasonObj) {
        returnValue = true;
      } else if (
        ["input", "select"].includes(selectedSignupReasonObj?.type || "") &&
        !requestData.signup_reason_etc
      ) {
        returnValue = true;
      }
    } else if (step === 1) {
      if (!["Y", "N"].includes(requestData.wedding_yn)) {
        returnValue = true;
      } else if (requestData.wedding_yn === "Y") {
        const tempdate = formatStringToDate({
          date: requestData.wedding_anniversary,
          delimiter: "-",
        });
        if (tempdate.length !== 10 || !moment(tempdate).isValid()) {
          returnValue = true;
        }
      } else if (requestData.wedding_yn === "N") {
        const tempdate = formatStringToDate({
          date: requestData.wedding_day,
          delimiter: "-",
        });
        if (tempdate.length !== 10 || !moment(tempdate).isValid()) {
          returnValue = true;
        }
        if (!requestData.wedding_hope_location) {
          returnValue = true;
        }
      }
    } else if (step === 2) {
      if (!requestData.nickname || !requestData.isCheckedNickname) {
        returnValue = true;
      } else if (
        !requestData.phone_number ||
        !requestData.authCode ||
        !requestData.userInputAuthCode ||
        !requestData.isAuthConfirm
      ) {
        returnValue = true;
      } else if (requestData.authCode !== requestData.userInputAuthCode) {
        returnValue = true;
      } else if (!requestData.isAuthConfirm) {
        returnValue = true;
      }
    } else if (step === 3) {
      const selectedSignupRouteObj = SIGNUP_ROUTE_LIST.find(
        (item) => item.title === requestData.signup_route,
      );
      if (!selectedSignupRouteObj) {
        returnValue = true;
      } else if (
        selectedSignupRouteObj.isRecommend &&
        !requestData.recommender
      ) {
        returnValue = true;
      } else if (!requestData.isCheckedNickname) {
        returnValue = true;
      }
    }
    return returnValue;
  }, [step, requestData]);

  const pageProps: IPageProps = { data: requestData, setData: setRequestData };

  const viewList = [
    <Page1 {...pageProps} />,
    <Page2 {...pageProps} />,
    <Page3 {...pageProps} />,
    <Page4 {...pageProps} />,
  ];

  const signOn = async () => {
    try {
      if (!signOnData) {
        return;
      }
      const filteredColumn = [
        requestData.sns_type,
        requestData.sns_id,
        requestData.sns_token,
        requestData.name,
        requestData.nickname,
      ].filter((data) => !isDefined(data));
      if (filteredColumn.length > 0) {
        return toast.error("데이터가 모두 입력되지 않았습니다.");
      }

      const signupParams: ISignupRequest = { ...requestData };

      const { message, success } = await post("/api/user/signup", signupParams);
      if (success) {
        if (!!signOnData.type && !!signOnData.id) {
          const { success: loginSuccess } = await login({
            sns_type: signOnData.type,
            sns_id: signOnData.id,
          });
          if (loginSuccess) {
            toast.success(message);
            navigate("/");
          }
        }
      } else {
        toast(message);
      }
    } catch (error: any) {
      toast.error(
        error?.message || "에러가 발생했습니다. 관리자에게 문의해주세요",
      );
    }
  };

  async function nextStep() {
    if (disabled) {
      return;
    }
    if (step === 0) {
      try {
      } catch (error: any) {
        return toast.error(
          error?.message || "에러가 발생했습니다. 관리자에게 문의해주세요",
        );
      }
    }
    if (step === 3) {
      if (!requestData.isAuthConfirm) {
        return toast.error("전화번호 인증을 완료해주세요.");
      } else if (requestData.authCode !== requestData.userInputAuthCode) {
        return toast.error("인증번호가 일치하지 않습니다.");
      }
    }
    if (step === 4) {
      signOn();
      return;
    }
    if (step === viewList.length - 1) {
      setIsOpenModal(true);
      return;
    }
    setStep(step + 1);
  }

  useEffect(() => {
    setHeader((prev) => ({
      ...prev,
      title: "회원가입",
      left: (
        <Icon
          name="back"
          onClick={() => {
            if (step === 0) {
              navigate(-1);
            } else {
              setStep(step - 1);
            }
          }}
        />
      ),
    }));
  }, [step]);

  return (
    <>
      <div className={styles.container}>
        <div
          className={styles.slideContainer}
          style={{
            width: `${viewList.length * 100}%`,
            left: `-${step * 100}%`,
          }}
        >
          {viewList}
        </div>
        <div className={styles.btnContainer}>
          <button
            disabled={disabled}
            onClick={nextStep}
            className={[styles.btn, !disabled && styles.active].join(" ")}
          >
            <FontText size={18} weight="500" color={white}>
              {step === viewList.length ? "가입하기" : "계속하기"}
            </FontText>
          </button>
        </div>
      </div>
      <ToastContainer
        position="bottom-center"
        limit={1}
        closeButton={false}
        autoClose={800}
        theme="light"
      />
      <SignonModal
        visible={isOpenModal}
        close={() => setIsOpenModal(false)}
        confirm={signOn}
      />
    </>
  );
}

function Page1({ data, setData }: IPageProps) {
  return (
    <div className={styles.page}>
      <div className={styles.pageTitle}>궁금하신 항목은 무엇인가요?</div>
      <div style={{ paddingTop: 16 }}>
        {SIGNUP_REASON_LIST.map((item, index) => {
          const isSelected = data.signup_reason === item.title;

          return (
            <div key={index}>
              <div
                className={styles.radio}
                onClick={() =>
                  setData({
                    ...data,
                    signup_reason: item.title,
                    signup_reason_etc: "",
                  })
                }
              >
                <div
                  className={[
                    styles.radioBtn,
                    !!isSelected && styles.radioSelected,
                  ].join(" ")}
                />
                <FontText color={gray1}>{item.title}</FontText>
              </div>
              {isSelected && (
                <>
                  {item.type === "input" && (
                    <Input
                      value={data.signup_reason_etc}
                      onChange={(value) =>
                        setData?.({ ...data, signup_reason_etc: value })
                      }
                      clear={
                        data.signup_reason_etc.length > 0
                          ? () => setData?.({ ...data, signup_reason_etc: "" })
                          : undefined
                      }
                      placeholder="내용 입력"
                      height={40}
                      containerClassName={styles.input}
                    />
                  )}
                  {item.type === "select" &&
                    (item.selectList || []).length > 0 && (
                      <div className={styles.selectList}>
                        {(item.selectList || []).map(
                          (selectItem, selecteIndex) => {
                            const isSelectedSelected =
                              selectItem === data.signup_reason_etc;
                            return (
                              <div
                                key={selecteIndex}
                                onClick={() =>
                                  setData?.({
                                    ...data,
                                    signup_reason_etc: selectItem,
                                  })
                                }
                                className={[
                                  styles.selectItem,
                                  isSelectedSelected && styles.selected,
                                ].join(" ")}
                              >
                                <FontText
                                  color={isSelectedSelected ? gray2 : gray5}
                                  lineHeight={25}
                                  size={14}
                                >
                                  {selectItem}
                                </FontText>
                              </div>
                            );
                          },
                        )}
                      </div>
                    )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Page2({ data, setData }: IPageProps) {
  return (
    <div className={styles.page}>
      <div>
        <div className={styles.selectList}>
          {(
            [
              { title: "미혼", value: "N" },
              { title: "기혼", value: "Y" },
            ] as { title: string; value: "Y" | "N" }[]
          ).map((item, selecteIndex) => {
            const isSelected = item.value === data.wedding_yn;
            return (
              <div
                key={selecteIndex}
                onClick={() =>
                  setData?.({
                    ...data,
                    wedding_yn: item.value,
                    wedding_day: "",
                    wedding_anniversary: "",
                    wedding_hope_location: "",
                  })
                }
                className={[
                  styles.selectItem,
                  isSelected && styles.selected,
                ].join(" ")}
              >
                <FontText
                  color={isSelected ? gray2 : gray5}
                  lineHeight={25}
                  size={14}
                >
                  {item.title}
                </FontText>
              </div>
            );
          })}
        </div>
      </div>
      {data.wedding_yn === "N" && (
        <div>
          <div>
            <FontText style={{ marginBottom: 8 }}>예식 지역</FontText>
            <Input
              containerClassName={styles.input}
              value={data.wedding_hope_location}
              onChange={(value) =>
                setData?.({ ...data, wedding_hope_location: value })
              }
              clear={
                data.wedding_hope_location.length > 0
                  ? () => setData?.({ ...data, wedding_hope_location: "" })
                  : undefined
              }
              placeholder={"예식 지역 또는 희망 지역을 입력해주세요."}
              height={40}
              style={{ marginTop: 0, marginBottom: 4 }}
            />
          </div>
          <div style={{ marginTop: 16 }}>
            <FontText style={{ marginBottom: 8 }}>예식일</FontText>
            <Input
              containerClassName={styles.input}
              type="date"
              value={
                moment(data.wedding_day).isValid()
                  ? moment(data.wedding_day).format("YYYY-MM-DD")
                  : undefined
              }
              onChange={(value) =>
                setData?.({
                  ...data,
                  wedding_day: formatStringToDate({ date: value }),
                })
              }
              bottomContents={
                <FontText size={12} color={gray5}>
                  예식일 미정일 경우 희망일로 입력해주세요.
                </FontText>
              }
              placeholder={`날짜 입력 (${moment().format("YYYY.MM.DD")})`}
              height={40}
              style={{ marginTop: 0, marginBottom: 4 }}
            />
          </div>
        </div>
      )}
      {data.wedding_yn === "Y" && (
        <div>
          <FontText style={{ marginBottom: 8 }}>결혼기념일</FontText>
          <Input
            containerClassName={styles.input}
            type="date"
            value={
              moment(data.wedding_anniversary).isValid()
                ? moment(data.wedding_anniversary).format("YYYY-MM-DD")
                : undefined
            }
            onChange={(value) =>
              setData?.({
                ...data,
                wedding_anniversary: formatStringToDate({ date: value }),
              })
            }
            placeholder={`날짜 입력 (${moment().format("YYYY.MM.DD")})`}
            height={40}
            style={{ marginTop: 0, marginBottom: 4 }}
          />
        </div>
      )}
    </div>
  );
}

function Page3({ data, setData }: IPageProps) {
  const isAuthCode = data.authCode.length > 0;

  const sendAuthCode = async () => {
    try {
      if (!data.phone_number) {
      }
      const code = Math.random().toFixed(24).substr(2, 4);
      await setData({ ...data, authCode: code, isAuthConfirm: false });
      console.log(code);
      const result = await post("/api/user/sns_send", {
        phone_number: data.phone_number,
        code,
      });
      toast?.(result.message);
    } catch (error) {
      console.log("sendAuthCode error", error);
    }
  };

  const checkAuthCode = () => {
    try {
      if (data.authCode === data.userInputAuthCode) {
        toast.error?.("인증이 완료되었습니다.");
        setData({ ...data, isAuthConfirm: true });
      } else {
        toast.error?.("인증번호가 올바르지 않습니다. 다시 입력해주세요.");
      }
    } catch (error) {
      console.error("checkAuthCode error", error);
    }
  };

  const checkNickname = async () => {
    try {
      const result = await post("/api/user/nicknamecheck", {
        nickname: data.nickname,
      });
      const { message, success } = result;
      if (success) {
        setData({ ...data, isCheckedNickname: true });
      } else {
        toast.error?.(message);
        setData({
          ...data,
          isCheckedNickname: false,
          nicknameErrorMsg: message,
        });
      }
    } catch (error: any) {
      console.error("checkNickname error", error);
      const message =
        typeof error === "string" ? error : "이미 사용중인 닉네임입니다.";
      toast.error?.(message);
      setData({ ...data, isCheckedNickname: false, nicknameErrorMsg: message });
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.selectList}>
        {(["남자", "여자"] as TGender[]).map((gender, index) => {
          const isSelected = gender === data.gender;
          return (
            <div
              key={index}
              onClick={() => setData?.({ ...data, gender })}
              className={[
                styles.selectItem,
                isSelected && styles.selected,
              ].join(" ")}
            >
              <FontText
                color={isSelected ? gray2 : gray5}
                lineHeight={25}
                size={14}
              >
                {gender}
              </FontText>
            </div>
          );
        })}
      </div>
      <div className={[styles.inputContainer].join(" ")}>
        <FontText style={{ marginBottom: 8 }}>이름</FontText>
        <Input
          value={data.name}
          onChange={(value) => setData?.({ ...data, name: value })}
          clear={
            data.name.length > 0
              ? () => setData?.({ ...data, name: "" })
              : undefined
          }
          placeholder="이름 입력"
          height={40}
          containerClassName={styles.input}
          limit={20}
          secondline={false}
        />
      </div>
      <div className={[styles.inputContainer].join(" ")}>
        <FontText style={{ marginBottom: 8 }}>닉네임</FontText>
        <div className={[styles.inputRow].join(" ")}>
          <Input
            onBlur={checkNickname}
            value={data.nickname}
            onChange={(value) =>
              setData?.({
                ...data,
                nickname: value,
                isCheckedNickname: false,
                nicknameErrorMsg: "",
              })
            }
            clear={
              data.nickname.length > 0
                ? () =>
                    setData?.({
                      ...data,
                      nickname: "",
                      isCheckedNickname: false,
                      nicknameErrorMsg: "",
                    })
                : undefined
            }
            placeholder="닉네임 입력"
            limit={20}
            height={40}
            containerClassName={styles.input}
            style={{ flex: 1 }}
            secondline={false}
          />
          <div
            onClick={data.isCheckedNickname ? undefined : checkNickname}
            className={[
              styles.inputRowBtn,
              data.isCheckedNickname && styles.disabled,
            ].join(" ")}
          >
            <FontText weight="500" color={white} align="center">
              {!data.isCheckedNickname ? "중복확인" : "사용가능"}
            </FontText>
          </div>
        </div>
      </div>
      <div className={[styles.inputContainer].join(" ")}>
        <FontText style={{ marginBottom: 8 }}>전화번호</FontText>
        <div className={[styles.inputRow].join(" ")}>
          <Input
            limit={11}
            value={data.phone_number}
            clear={
              data.phone_number.length > 0
                ? () => setData?.({ ...data, phone_number: "" })
                : undefined
            }
            onChange={(value) => setData?.({ ...data, phone_number: value })}
            placeholder="'-'를 제외한 전화번호를 입력해주세요"
            height={40}
            containerClassName={styles.input}
            readOnly={data.isAuthConfirm}
            secondline={false}
          />
          <div
            onClick={data.isAuthConfirm ? undefined : sendAuthCode}
            className={[
              styles.inputRowBtn,
              data.isAuthConfirm && styles.disabled,
            ].join(" ")}
          >
            <FontText weight="500" color={white} align="center">
              {data.isAuthConfirm
                ? "인증완료"
                : `인증번호 ${isAuthCode ? "재발송" : "발송"}`}
            </FontText>
          </div>
        </div>
      </div>
      <div className={[styles.inputContainer].join(" ")}>
        {isAuthCode && !data.isAuthConfirm && (
          <div className={[styles.inputRow].join(" ")}>
            <Input
              limit={4}
              value={data.userInputAuthCode}
              clear={
                data.userInputAuthCode.length > 0
                  ? () => setData?.({ ...data, userInputAuthCode: "" })
                  : undefined
              }
              onChange={(value) =>
                setData?.({ ...data, userInputAuthCode: value })
              }
              placeholder="인증코드 4자리"
              height={40}
              containerClassName={styles.input}
              secondline={false}
            />
            <div
              onClick={checkAuthCode}
              className={[styles.inputRowBtn].join(" ")}
            >
              <FontText weight="500" color={white} align="center">
                인증하기
              </FontText>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Page4({ data, setData }: IPageProps) {
  return (
    <div className={styles.page}>
      <div className={styles.pageTitle}>가입경로를 알려주세요.</div>
      <div style={{ paddingTop: 16 }}>
        {SIGNUP_ROUTE_LIST.map((item, index) => {
          const isSelected = data.signup_route === item.title;

          return (
            <div key={index}>
              <div
                className={styles.radio}
                onClick={() =>
                  setData({
                    ...data,
                    signup_route: item.title,
                    recommender: "",
                  })
                }
              >
                <div
                  className={[
                    styles.radioBtn,
                    !!isSelected && styles.radioSelected,
                  ].join(" ")}
                />
                <FontText color={gray1}>{item.title}</FontText>
              </div>
              {!!isSelected && !!item.isRecommend && (
                <div className={[styles.inputRow, { marginTop: 16 }].join(" ")}>
                  <Input
                    value={data.recommender}
                    onChange={(value) =>
                      setData?.({ ...data, recommender: value })
                    }
                    containerClassName={styles.input}
                    clear={
                      data.recommender.length > 0
                        ? () => setData?.({ ...data, recommender: "" })
                        : undefined
                    }
                    placeholder="추천인 닉네임 입력"
                    bottomContents="추천해주신 분과, 추천받으신 분께 1,000p를 적립해드려요! :)"
                    limit={20}
                    height={40}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SignonModal({
  visible,
  close,
  confirm,
}: {
  visible: boolean;
  close?: () => void;
  confirm?: () => void;
}) {
  const [terms, setTerms] = useState<{ [key in TTermsKey]: boolean }>({
    service: false,
    private: false,
  });
  const isAllChecked =
    Object.keys(terms).filter((key) => !terms[key as TTermsKey]).length === 0;
  const isRequiredAllChecked =
    Object.keys(terms)
      .filter((key) => !!TERMS_LIST.find((item) => item.key === key)?.require)
      .filter((key) => !terms[key as TTermsKey]).length === 0;

  const allChecking = () => {
    if (isAllChecked) {
      setTerms({ service: false, private: false });
    } else {
      setTerms({ service: true, private: true });
    }
  };

  return (
    <Modal
      visible={!!visible}
      isCenter={true}
      backOpacity={70}
      className={styles.signOnModal}
      close={close}
      toWay="top"
    >
      <div className={styles.wrap} onClick={close}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <FontText weight="bold" size={24} lineHeight={31}>
            {"서비스 이용약관에\n동의해 주세요."}
          </FontText>
          <div
            onClick={allChecking}
            className={[
              styles.allConfirmBtn,
              isAllChecked && styles.active,
            ].join(" ")}
          >
            <div
              className={[
                styles.svgContainer,
                isAllChecked && styles.active,
              ].join(" ")}
            >
              <SvgcheckLine fill={isAllChecked ? mainColor : gray6} />
            </div>
            <FontText
              weight="500"
              color={isAllChecked ? mainColor : gray6}
              align="center"
            >
              모든 항목에 동의합니다.
            </FontText>
          </div>
          {TERMS_LIST.map((item, index) => {
            const isSelected = terms[item.key];
            return (
              <div
                key={item.key}
                className={[styles.termsItem, isSelected && styles.active].join(
                  " ",
                )}
                onClick={() =>
                  setTerms((prev) => ({ ...prev, [item.key]: !isSelected }))
                }
              >
                <div
                  className={[
                    styles.svgContainer,
                    isSelected && styles.active,
                  ].join(" ")}
                >
                  <SvgcheckLine fill={isSelected ? mainColor : gray6} />
                </div>
                <FontText
                  weight="500"
                  color={isSelected ? mainColor : gray6}
                  align="center"
                  style={{ flex: 1 }}
                >
                  {!!item.require ? "(필수) " : ""}
                  {item.title}
                </FontText>
                {!!item.link && (
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      window.open(item.link);
                    }}
                    className={styles.arrow}
                  >
                    <div>
                      <SvgLineArrow width={10} direction="Up" />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <div
            onClick={isRequiredAllChecked ? confirm : undefined}
            className={[
              styles.confirmBtn,
              isRequiredAllChecked && styles.active,
            ].join(" ")}
            style={{ cursor: "pointer" }}
          >
            <FontText weight="500" color={white} align="center">
              회원가입
            </FontText>
          </div>
        </div>
      </div>
    </Modal>
  );
}
